import { useCallback, useEffect, useMemo, useState } from 'react';
import { Cell } from 'react-table';
import { useRouteMatch, useHistory } from 'react-router';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { TableColumn, TableRow, TableCell } from '@/components/atoms/Table/Table.type';
import { useGetBrandRestaurantsQuery } from '@/services/innovorder/brand/brand.endpoints';
import { computeText } from '@/locales/utils';
import { formatDate } from '@/utils/date';
import { useAuthCookieHandler } from '@/hooks/useAuthCookieHandler/useAuthCookieHandler';
import { redirectToBackofficeV1 } from '@/utils/backoffice-v1';
import { UserRoles } from '@/services/innovorder/user/user.type';
import { getUserBrandId, getUserRole } from '@/redux/user';
import { Routes } from '@/core/router/routes.types';
import { RestaurantAddress } from '@/services/innovorder/restaurant/restaurant.type';
import { getUseNewRestaurantInfoPage } from '@/redux/launchDarkly/launchDarkly.slice';
import { fetchDataProps } from '../../components/atoms/Table/Table';

interface BrandRestaurantsListViewModel {
    rows: TableRow[];
    columns: TableColumn[];
    isLoading: boolean;
    brandId: string;
    isNewRestaurantModalOpen: boolean;
    canCreateRestaurant: boolean;
    openNewRestaurantModal: () => void;
    closeNewRestaurantModal: () => void;
    handleRowClick: (
        cell: Cell<TableRow, TableCell<unknown>> | undefined,
        event?: React.MouseEvent<HTMLTableCellElement>,
    ) => void;
    fetchData: (fetchProps: fetchDataProps) => void;
    fetchDataTotalCount: number | undefined;
}

const formatAddress = (address: RestaurantAddress | null): string => {
    if (!address) return '';
    const parts = [address.streetNumber, address.route].filter(Boolean);
    return parts.join(' ');
};

const useBrandRestaurantsListVM = (): BrandRestaurantsListViewModel => {
    const intl = useIntl();
    const history = useHistory();
    const { params } = useRouteMatch<{ brandId: string }>();
    const { brandId } = params;

    const userRole = useSelector(getUserRole);
    const currentUserBrandId = useSelector(getUserBrandId);
    const useNewRestaurantInfoPage = useSelector(getUseNewRestaurantInfoPage);
    const { token } = useAuthCookieHandler();

    const canCreateRestaurant = userRole === UserRoles.admin;

    const { data, isLoading } = useGetBrandRestaurantsQuery(
        { brandId: Number(brandId), token: token || '' },
        { skip: !token },
    );

    const [isNewRestaurantModalOpen, setIsNewRestaurantModalOpen] = useState(false);
    const openNewRestaurantModal = () => setIsNewRestaurantModalOpen(true);
    const closeNewRestaurantModal = () => setIsNewRestaurantModalOpen(false);

    const [filteredData, setFilteredData] = useState(data);
    const fetchData = useCallback(
        ({ globalFilter }: fetchDataProps) => {
            if (!data) {
                setFilteredData([]);
                return;
            }
            if (!globalFilter) {
                setFilteredData(data);
                return;
            }
            const lowerFilter = globalFilter.toLowerCase();
            setFilteredData(
                data.filter(
                    (r) =>
                        r.name.toLowerCase().includes(lowerFilter) ||
                        String(r.restaurantId).includes(lowerFilter) ||
                        formatAddress(r.address).toLowerCase().includes(lowerFilter),
                ),
            );
        },
        [data],
    );

    useEffect(() => {
        setFilteredData(data);
    }, [data]);

    const rows: TableRow[] = useMemo(() => {
        if (!filteredData) return [];
        return filteredData.map((restaurant) => ({
            restaurantId: {
                key: restaurant.restaurantId,
                type: 'string',
                value: {
                    text: String(restaurant.restaurantId),
                },
            },
            name: {
                type: 'string',
                value: {
                    text: restaurant.name,
                },
            },
            address: {
                type: 'string',
                value: {
                    text: formatAddress(restaurant.address),
                },
            },
            createdAt: {
                type: 'string',
                value: {
                    text: formatDate(restaurant.created_at),
                },
            },
        }));
    }, [filteredData]);

    const columns: TableColumn[] = useMemo(
        () => [
            {
                Header: 'ID',
                accessor: 'restaurantId',
                disableFilters: true,
                disableSortBy: true,
            },
            {
                Header: computeText(intl, 'name.name'),
                accessor: 'name',
                disableFilters: true,
                disableSortBy: true,
            },
            {
                Header: computeText(intl, 'restaurantInfo.address'),
                accessor: 'address',
                disableFilters: true,
                disableSortBy: true,
            },
            {
                Header: computeText(intl, 'date.created'),
                accessor: 'createdAt',
                disableFilters: true,
                disableSortBy: true,
            },
        ],
        [intl],
    );

    const handleRowClick = useCallback(
        (cell: Cell<TableRow, TableCell<unknown>> | undefined, event?: React.MouseEvent<HTMLTableCellElement>) => {
            if (!cell) return;
            const restaurantId = cell.row.values.restaurantId.key;
            if (useNewRestaurantInfoPage) {
                history.push(`/brands/${brandId}/restaurants/${restaurantId}/info`);
            } else {
                redirectToBackofficeV1(`/home/brands/${brandId}/restaurants/${restaurantId}/info`, event?.ctrlKey);
            }
        },
        [brandId, useNewRestaurantInfoPage, history],
    );

    useEffect(() => {
        if (userRole !== UserRoles.admin && currentUserBrandId !== Number(brandId)) {
            history.replace(Routes.PermissionDenied);
        }
    }, [currentUserBrandId, brandId, history, userRole]);

    return {
        rows,
        columns,
        isLoading,
        brandId,
        isNewRestaurantModalOpen,
        canCreateRestaurant,
        openNewRestaurantModal,
        closeNewRestaurantModal,
        handleRowClick,
        fetchData,
        fetchDataTotalCount: filteredData?.length,
    };
};

export default useBrandRestaurantsListVM;
